import { useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'

/**
 * Handles redirects from old hash-based URLs (/#/...) to new path-based URLs.
 * This ensures backwards compatibility with links from the old frontend.
 *
 * Supported legacy routes:
 * - /#/reset-password?token=xxx -> /reset-password?token=xxx
 * - /#/activate-account?token=xxx -> /activate?token=xxx
 * - /#/conversations/{id}/show -> /conversations/{id}/overview
 * - /#/conversations/redirect/{twilio_id} -> /conversations/redirect/{twilio_id}
 */
export function LegacyHashRedirectHandler() {
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    const hash = window.location.hash

    // Only process if there's a hash that looks like an old route
    if (!hash || !hash.startsWith('#/')) {
      return
    }

    // Extract the path and query from the hash
    const hashContent = hash.slice(1) // Remove the leading #
    const [hashPath, hashQuery] = hashContent.split('?')
    const queryString = hashQuery ? `?${hashQuery}` : ''

    // Define redirect mappings
    let newPath: string | null = null

    // /#/reset-password -> /reset-password
    if (hashPath === '/reset-password') {
      newPath = `/reset-password${queryString}`
    }
    // /#/activate-account -> /activate
    else if (hashPath === '/activate-account') {
      newPath = `/activate${queryString}`
    }
    // /#/conversations/{id}/show -> /conversations/{id}/overview
    else if (hashPath.match(/^\/conversations\/([^/]+)\/show$/)) {
      const match = hashPath.match(/^\/conversations\/([^/]+)\/show$/)
      if (match) {
        const id = match[1]
        newPath = `/conversations/${id}/overview${queryString}`
      }
    }
    // /#/conversations/redirect/{twilio_id} -> /conversations/redirect/{twilio_id}
    else if (hashPath.match(/^\/conversations\/redirect\/([^/]+)$/)) {
      const match = hashPath.match(/^\/conversations\/redirect\/([^/]+)$/)
      if (match) {
        const twilioId = match[1]
        newPath = `/conversations/redirect/${twilioId}${queryString}`
      }
    }

    if (newPath) {
      // Clear the hash and navigate to the new path
      window.history.replaceState(null, '', window.location.pathname)
      navigate(newPath, { replace: true })
    }
  }, [navigate, location])

  return null
}
